import { IGetFairValueEvaluationAssetBookValue } from './../models/FairValueEvaluation';
import { AxiosError } from "axios"
import { Operation } from "fast-json-patch"
import dayjs, { Dayjs } from "utils/dayjs"

import ApiService from "./ApiService"
import { ApiLinkRel, ApiLinkEntity, ApiServiceName, ErrorCode, Purpose, SortDirection } from "config/constants"
import { Guid, IMultipleEntities, IMultipleEntitiesResponse, IApiLink, IApiFollowLink, Lmid } from "@/types"
import Invoice, { IGetInvoiceDto, IPostInvoiceDto, IPostInvoiceAssetAssignmentDto, InvoiceAutomaticAssignStatus } from "models/Invoice"
import Valuation, { IGetValuationDto, ValuationStatus, ValuationsSortBy } from "models/Valuation"
import CustomError from "models/CustomError"
import Depreciation, { IGetDepreciationDto } from "models/Depreciation"
import EvaluationCommonInfo, { IGetEvaluationCommonInfoDto } from "models/EvaluationCommonInfo"
import FinancialFactors, { IPostFinancialFactorsDto, IGetFinancialFactorsDto } from "models/FinancialFactors"
import CalculationResult, { IUpdateCalculationResultSaleDto, IUpdateCalculationResultRespDto, IUpdateOfferCalculationResultSaleDto, CalculationParamsValidation } from "models/CalculationResult"
import { IGetCalculationDetailsSaleDto, CalculationDetailsSale, CalculationDetailsSaleExtended, IGetCalculationDetailsSaleExtendedDto } from "models/CalculationDetails"
import { ICreateCostCategoryDto, IGetCostCategoryDto } from "models/CostCategory"
import CostCategories from "models/CostCategories"
import { hasMorePages } from "./utils"
import DepreciationPeriods, { IGetDepreciationPeriodsDto } from "models/DepreciationPeriods"
import InvoiceDetails, { IGetInvoiceDetailsDto } from "models/InvoiceDetails"
import { ImputedInterestCalculation, IGetImputedInterestCalculationDto } from "models/ImputedInterestCalculation"
import { IGetLocalDepreciationDto, LocalDepreciation } from "models/LocalDepreciation"
import AssetInvoiceIncoming, { IGetAssetInvoiceIncomingDto, InvoiceCategoryType } from "models/AssetInvoiceIncoming"
import AssetInvoiceOutgoingByRequest, { AssetInvoiceOutgoingType, IGetAssetInvoiceOutgoingByRequestDto } from "models/AssetInvoiceOutgoing"
import { AssetCategory, IGetAssetCategoryDto } from "models/AssetCategory"
import EvaluationParameters, { IGetEvaluationParametersDto, IGetEvaluationParametersParams } from "models/EvaluationParameters"
import EvaluationParametersPeriods, { IGetEvaluationParametersPeriodsDto } from 'models/EvaluationParametersPeriods';
import { ICreateEvaluationParametersPeriodDto } from './../models/EvaluationParametersPeriod';
import EvaluationParametersSettings, { IGetEvaluationParametersSettingsDto } from './../models/EvaluationParametersSettings';
import { ICreateEvaluationParameterDto } from "models/EvaluationParameter"
import { FinanceServiceAssetType, FinanceServiceAssetTypeAvailability, IGetFinanceServiceAssetTypeDto } from "models/FinanceServiceAssetType"
import MonthlyClosings, { IGetMonthlyClosingsDto } from "models/MonthlyClosings"
import { ICreateMonthlyClosingDto } from "models/MonthlyClosing"
import { downloadBlobAsFile, toDateTimeWithoutTimezone } from "utils/utils"
import AsyncOperation, { IGetAsyncOperationStatusDto } from "models/AsyncOperationStatus"
import AmountWithCurrency, { IAmountWithCurrencyDto } from "models/AmountWithCurrency"
import { IReadSpecialDepreciationBookValueDto, IUpdateSpecialDepreciationDto } from "models/SpecialDepreciation"
import { IUpdateDepreciationPeriodIfrsDto } from "models/DepreciationPeriodIfrs"
import ArticleService from "./ArticleService"
import { ICreateOverheadFactorsPeriodDto } from "models/OverheadFactorsPeriod"
import OverheadFactorsPeriods, { IOverheadFactorsPeriodsDto } from "models/OverheadFactorsPeriods"
import OverheadFactors, { IOverheadFactorsDto, OverheadFactorsPhase } from "models/OverheadFactors"
import { IOverheadFactorDto } from "models/OverheadFactor"
import { AssetOrigin } from "models/Asset"
import TaxBasedDeductions, { ITaxBasedDeductionsDto } from "models/TaxBasedDeductions"
import { ICreateTaxBasedDeductionDto } from "models/TaxBasedDeduction"
import OverheadFactorsSettings, { IOverheadFactorsSettingsDto } from "models/OverheadFactorsSettings"
import AssetCategoriesAndDivisions, { AssetCategoriesAndDivisionsAvailability, IGetAssetCategoriesAndDivisionsDto } from "models/AssetCategoriesAndDivisions"
import AssetReadyToSplit, { IGetAssetReadyToSplitDto } from './../models/AssetReadyToSplit';
import ErrorHandler from "utils/errorHandler"
import Supplier from 'models/Supplier';
import { IGetSupplierDto } from './../models/Supplier';
import { IPostImputedInterestRateDto } from './../models/ImputedInterestRate';
import InvoiceAdditionalAssetToAssign, { IInvoiceAdditionalLmidToAssign } from "models/InvoiceAdditionalAssetsToAssign"
import BookingForSelectedAsset from 'models/BookingForSelectedAsset';
import { IGetBookingForSelectedAssetDto } from './../models/BookingForSelectedAsset';
import AssetBookingViews, { IGetAssetBookingViewsDto } from './../models/AssetBookingViews';
import { IGetInvoiceAdditionalAssetToAssignDto } from './../models/InvoiceAdditionalAssetsToAssign';
import { IPostCalculationDetailsVersion } from "models/CalculationDetailsVersion"
import CalculationDetailsVersions from "models/CalculationDetailsVersions"
import { IGetCalculationDetailsVersionsDto } from './../models/CalculationDetailsVersions';
import InvoiceImportOperation, { IGetInvoiceImportOperationDto, InvoiceImportError } from "models/InvoiceImportOperation"
import AssetAcquisition, { IGetAssetAcqiusitionDTO } from "models/AssetAcqiusition"
import { IGetOverheadFactorsParams } from './../models/OverheadFactors';
import CalculationDetailsVersionComment, { IGetCalculationDetailsVersionCommentDto, IPostCalculationDetailsVersionCommentDto } from 'models/CalculationDetailsVersionComment';
import CalculationDetailsVersionComments, { IGetCalculationDetailsVersionCommentsDto } from './../models/CalculationDetailsVersionComments';
import AssetArticle from "models/AssetArticle"
import FairValueFactors, { IGetFairValueFactorsDto, IPostFairValueFactorsDto } from "models/FairValueFactors"
import { Division, IGetDivisionDto } from "models/Division"
import { DepreciationMatrix, ExceptionDepreciationMatrix, IGetDepreciationMatrixDto, IGetExceptionDepreciationMatrixDto, IPostDepreciationMatrixDto, IPostExceptionDepreciationMatrixDto } from "models/DepreciationMatrix"
import { FairValueEvaluationAssetBookValue, FairValueEvaluationSortBy, FairValueReferenceNumberEvaluationItem, IFairValueEvaluationPeriod, IGetFairValueReferenceNumberEvaluationItem } from "models/FairValueEvaluation"

interface IGetInvoicesResp extends IMultipleEntities<Invoice> {
    totalAmount?: AmountWithCurrency
    createInvoiceLink?: IApiLink
}

export interface IGetBookingPeriodResp {
    bookingPeriod: string
}

export interface IValidateCalculationDetailsOverheadFactorsResp {
    paramsValidationStatus: CalculationParamsValidation[]
}

export interface IGetAssetIncomingInvoicesParams {
    assetId: Guid
    pageSize?: number
    pageNumber?: number
    type?: InvoiceCategoryType
    costCategoryId?: Guid
}

export interface FairValueEvaluationParams {
    userProfitCenterIds: Guid[]
    profitCenterIds?: Guid[]
    assetCategories?: Guid[]
    referenceNumberSale?: string[]
    missingDepreciationRate?: boolean
    onlyReferenceNumbersWithMachines?: boolean
    pageSize?: number
    pageNumber?: number
    sortBy?: FairValueEvaluationSortBy
    sortDirection?: SortDirection
}

class FinanceService {

    async getInvoices(params?: {
        assignmentStatuses?: boolean,
        invoiceNumbers?: string[],
        referenceNumbers?: string[],
        referenceTexts?: string[],
        reservationNumbers?: string[],
        invoiceDate?: string,
        pageNumber?: number,
        pageSize?: number,
        showNotRelevant?: boolean,
    }): Promise<IGetInvoicesResp | null> {
        try {
            const data: IMultipleEntitiesResponse<IGetInvoiceDto> & { totalAmount: IAmountWithCurrencyDto } = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.invoice,
                    rel: ApiLinkRel.all
                },
                data: params
            })

            const createInvoiceLink = data.links?.find(lnk => lnk.entity === ApiLinkEntity.invoice && lnk.rel === ApiLinkRel.new)
            return {
                totalAmount: data.totalAmount && new AmountWithCurrency(data.totalAmount),
                list: data.entities.map(dto => new Invoice(dto)),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getInvoices({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
                createInvoiceLink
            }
        } catch (e) { }
        return null
    }

    async createInvoice(postInvoiceDto: IPostInvoiceDto, link: IApiLink): Promise<{ id?: Guid, automaticAssignStatus?: InvoiceAutomaticAssignStatus, requestNumberValidationFailed?: boolean }> {
        try {
            const res = await ApiService.followLink({
                link,
                data: postInvoiceDto
            })
            return res
        } catch (e) {
            if (e.isAxiosError && e.response && e.response.status === 402) {
                return { requestNumberValidationFailed: true }
            }
            ErrorHandler.handleHttpError(e)
        }
    }

    async getInvoice(id: Guid): Promise<Invoice | null> {
        try {
            const dto: IGetInvoiceDto = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.invoice,
                    rel: ApiLinkRel.self,
                    params: { id }
                }
            })

            return new Invoice(dto)
        } catch (e) {
        }
        return null
    }

    async updateInvoice(link: IApiLink, patch: Operation[]): Promise<{ automaticAssignStatus?: InvoiceAutomaticAssignStatus, requestNumberValidationFailed?: boolean }> {
        try {
            const res = await ApiService.followLink({
                link,
                data: patch,
            })
            return res
        } catch (e) {
            if (e.isAxiosError && e.response && e.response.status === 402) {
                return { requestNumberValidationFailed: true }
            }
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getInvoiceDetails(link: IApiLink): Promise<InvoiceDetails> {
        try {
            const dto: IGetInvoiceDetailsDto = await ApiService.followLink({
                link
            })
            return new InvoiceDetails(dto)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async setInvoiceNotRelevant(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({
                link
            })
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getSuppliers(): Promise<Supplier[]> {
        try {
            const suppliers: IGetSupplierDto[] = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.supplier,
                    rel: ApiLinkRel.all,
                },
            })

            return suppliers.map(supplierDto => new Supplier(supplierDto))
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getAssetBookingViews(link: IApiLink): Promise<AssetBookingViews> {
        try {
            const data: IGetAssetBookingViewsDto = await ApiService.followLink({ link })
            return new AssetBookingViews(data)
        } catch (e) {
            // Check assetSplitting conflict Error
            const isConflictError = e.isAxiosError && (e as AxiosError<any>).response?.status === 409
            throw new CustomError({
                code: isConflictError ? ErrorCode.conflict : ErrorCode.systemError
            })
        }
    }
    async getBookingsForSelectedAssets(link: IApiLink, assetIds: Guid[]): Promise<BookingForSelectedAsset[]> {
        try {
            const data: IGetBookingForSelectedAssetDto[] = await ApiService.followLink({ link, data: assetIds })
            return data.map(dto => new BookingForSelectedAsset(dto))
        } catch (e) {
            const isConflictError = e.isAxiosError && (e as AxiosError<any>).response?.status === 409
            throw new CustomError({
                code: isConflictError ? ErrorCode.forbidden : ErrorCode.systemError
            })
        }
    }

    async getInvoiceAdditionalLmidsToAssign(
        link: IApiLink,
        params: { criteria?: string; pageNumber?: number; pageSize?: number } = {}
    ): Promise<IMultipleEntities<IInvoiceAdditionalLmidToAssign>> {
        const { criteria, pageNumber = 1, pageSize = 50 } = params
        try {
            const data: IMultipleEntitiesResponse<IInvoiceAdditionalLmidToAssign> = await ApiService.followLink({
                link,
                linkQuery: {
                    Criteria: criteria || undefined,
                    PageNumber: pageNumber,
                    PageSize: pageSize,
                },
            })
            return {
                list: data.entities,
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata)
                    ? () => this.getInvoiceAdditionalLmidsToAssign(link, { criteria, pageNumber: data.paginationMetadata!.currentPage + 1, pageSize })
                    : undefined,
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getAdditionalAssetToAssign(link: IApiLink, assetId: Guid): Promise<InvoiceAdditionalAssetToAssign> {
        try {
            const dto: IGetInvoiceAdditionalAssetToAssignDto = await ApiService.followLink({ link, linkParams: { id: assetId } })
            return new InvoiceAdditionalAssetToAssign(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createInvoiceAssetAssignments({
        invoiceAssetAssignments,
        link
    }: {
        invoiceAssetAssignments: IPostInvoiceAssetAssignmentDto[],
        link: IApiLink
    }): Promise<void> {
        await ApiService.followLink({
            link,
            data: invoiceAssetAssignments
        })
    }

    async getValuations(params?: {
        lmids?: string[]
        statuses?: ValuationStatus[]
        availabilityDateFrom?: string
        availabilityDateTo?: string
        sortBy?: ValuationsSortBy
        sortDirection?: SortDirection
        pageNumber?: number
        pageSize?: number
    }): Promise<IMultipleEntities<Valuation> | null> {
        try {
            const data: IMultipleEntitiesResponse<IGetValuationDto> = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.valuation,
                    rel: ApiLinkRel.all,
                },
                data: params
            })

            const assetIds = data.entities.map(e => e.assetId)
            let assetArticlesList: AssetArticle[] = []
            if (assetIds && assetIds.length) {
                const assetArticles = await ArticleService.getAssetArticles({
                    assetIds,
                    pageSize: 5000,
                })
                assetArticlesList = assetArticles.list
            }

            return {
                list: data.entities.map(dto => {
                    return new Valuation({
                        ...dto,
                        linkedToAssetCollection: assetArticlesList.find(aa => aa.assetId === dto.assetId)?.linkedToAssetCollection
                    })
                }),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getValuations({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined
            }
        } catch (e) { }
        return null
    }

    async getValuationStatuses(): Promise<IMultipleEntities<ValuationStatus>> {
        try {
            const data: string[] = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.valuationStatus,
                    rel: ApiLinkRel.all,
                },
            })

            return {
                list: data.map((status => status.toLowerCase() as ValuationStatus)),
            }
        } catch (e) {
            throw new CustomError({
                messageCode: "message.systemError"
            })
        }
    }

    async getDepreciations(params: {
        valuationParameterId: Guid
    }): Promise<IMultipleEntities<Depreciation, EvaluationCommonInfo>> {
        try {
            const data: IMultipleEntitiesResponse<IGetDepreciationDto, IGetEvaluationCommonInfoDto> = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.depreciation,
                    rel: ApiLinkRel.all,
                    params: { id: params.valuationParameterId },
                },
            })

            if (!data.aggregates || !data.entities) {
                throw new CustomError({
                    code: ErrorCode.systemError,
                })
            }

            return {
                list: data.entities.map(dto => new Depreciation(dto)),
                aggregates: new EvaluationCommonInfo(data.aggregates),
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getLocalDepreciations(params: {
        valuationParameterId: Guid
    }): Promise<IMultipleEntities<LocalDepreciation, { evaluationCommonInfo: EvaluationCommonInfo, notCalculated?: boolean }>> {
        try {
            const data: IMultipleEntitiesResponse<IGetLocalDepreciationDto, IGetEvaluationCommonInfoDto> = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.localDepreciation,
                    rel: ApiLinkRel.all,
                    params: { id: params.valuationParameterId },
                },
            })

            if (!data.aggregates || !data.entities) {
                throw new CustomError({
                    code: ErrorCode.systemError,
                })
            }

            return {
                list: data.entities?.map(dto => new LocalDepreciation(dto)) || [],
                aggregates: {
                    evaluationCommonInfo: new EvaluationCommonInfo(data.aggregates),
                    notCalculated: data.entities === null
                }
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getImputedInterestCalculation(params: {
        valuationParameterId: Guid
    }): Promise<IMultipleEntities<ImputedInterestCalculation>> {
        try {
            const data: { entities: IGetImputedInterestCalculationDto[] | null } = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.imputedInterest,
                    rel: ApiLinkRel.all,
                    params: { id: params.valuationParameterId },
                },
            })

            if (!data.entities) {
                throw new CustomError({
                    code: ErrorCode.systemError,
                })
            }

            return {
                list: data.entities.map(dto => new ImputedInterestCalculation(dto)),
            }
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getCalculationDetails({
        purpose,
        reservationCartId,
        reservationId,
        versionId
    }: {
        purpose: Purpose.sale
        reservationCartId?: Guid
        reservationId?: Guid
        versionId?: Guid
    }): Promise<CalculationDetailsSale> {
        try {
            const query: IApiFollowLink["query"] = {
                ReservationCartId: reservationCartId,
                ReservationId: reservationId,
                Purpose: purpose,
                versionId
            }
            const dto: IGetCalculationDetailsSaleDto = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.calculationDetails,
                    rel: ApiLinkRel.self,
                    params: { query },
                },
            })


            return new CalculationDetailsSale(dto)
        } catch (e) {
            throw this.#handleGetCalculationDetailsError(e)
        }
    }


    async getCalculationDetailsExtended(link: IApiLink): Promise<{
        machines: CalculationDetailsSaleExtended | null,
        equipment: CalculationDetailsSaleExtended | null,
    }> {
        try {
            const dto: {
                machines: IGetCalculationDetailsSaleExtendedDto | null,
                equipment: IGetCalculationDetailsSaleExtendedDto | null
            } = await ApiService.followLink({ link })
            return {
                machines: dto.machines ? new CalculationDetailsSaleExtended(dto.machines) : null,
                equipment: dto.equipment ? new CalculationDetailsSaleExtended(dto.equipment) : null,
            }
        } catch (e) {
            throw this.#handleGetCalculationDetailsError(e)
        }
    }

    async getCalculationDetailsVersions(reservationCartId?: Guid, reservationId?: Guid): Promise<CalculationDetailsVersions> {
        try {
            const query: IApiFollowLink["query"] = {
                ReservationCartId: reservationCartId,
                ReservationId: reservationId,
            }
            const dto: IGetCalculationDetailsVersionsDto = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.saleCalculationDetailsVersionInfo,
                    rel: ApiLinkRel.all,
                    params: { query }
                },
            })
            return new CalculationDetailsVersions(dto)
        } catch (e) {
            throw this.#handleGetCalculationDetailsError(e)
        }
    }

    async createCalculationDetailsVersion(link: IApiLink, data: IPostCalculationDetailsVersion) {
        try {
            await ApiService.followLink({
                link,
                data
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getCalculationDetailsVersionComments(link): Promise<CalculationDetailsVersionComments> {
        try {
            const dto: IGetCalculationDetailsVersionCommentsDto = await ApiService.followLink({
                link
            })
            return new CalculationDetailsVersionComments(dto)
        } catch (e) {
            throw this.#handleGetCalculationDetailsError(e)
        }
    }

    async createCalculationDetailsVersionComment(link: IApiLink, data: IPostCalculationDetailsVersionCommentDto): Promise<CalculationDetailsVersionComment> {
        try {
            const dto: IGetCalculationDetailsVersionCommentDto = await ApiService.followLink({
                link,
                data
            })
            return new CalculationDetailsVersionComment(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    #handleGetCalculationDetailsError(e: any) {
        if (!e.isAxiosError) {
            return new CustomError({
                code: ErrorCode.systemError,
            })
        }
        const error = e as AxiosError<any>
        if (error.response && error.response.status === 404) {
            return new CustomError({
                code: ErrorCode.notFound,
            })
        } else if (error.response && error.response.status === 422) {
            return new CustomError({
                code: ErrorCode.unprocessableEntity,
            })
        } else {
            return new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async updateCalculationResult(
        data: IUpdateCalculationResultSaleDto,
        reservationCartId: Guid,
        reservationId?: Guid
    ): Promise<{
        result: CalculationResult | null,
        isAvailable: boolean
    }> {
        try {
            const query: IApiFollowLink["query"] = {
                ReservationCartId: reservationCartId,
                ReservationId: reservationId,
            }

            const dto: IUpdateCalculationResultRespDto = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.calculationDetails,
                    rel: ApiLinkRel.update,
                    params: { query },
                },
                data
            })

            return { result: new CalculationResult(dto), isAvailable: true }
        } catch (e) {
            if (e.isAxiosError) {
                const error = e as AxiosError<any>
                if (error.response?.status === 409) {
                    return { result: null, isAvailable: false }
                }
            }
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async validateCalculationDetailsOverheadFactors(reservationCartId: Guid): Promise<CalculationParamsValidation[]> {
        try {
            const dto: IValidateCalculationDetailsOverheadFactorsResp = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.calculationDetailsOverheadValidationReservationCart,
                    rel: ApiLinkRel.self,
                    params: {
                        query: {
                            ReservationCartId: reservationCartId
                        }
                    },
                }
            })

            return dto.paramsValidationStatus
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getOfferCalculationDetails(id: Guid): Promise<CalculationDetailsSale> {
        try {
            const dto: IGetCalculationDetailsSaleDto = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.offerCalculation,
                    rel: ApiLinkRel.self,
                    params: { id }
                },
            })


            return new CalculationDetailsSale(dto)
        } catch (e) {
            throw this.#handleGetCalculationDetailsError(e)
        }
    }

    async updateOfferCalculationResult(
        data: IUpdateOfferCalculationResultSaleDto,
        offerCalculationId?: Guid,
    ): Promise<{
        result: CalculationResult | null,
        id: Guid | null,
        isAvailable: boolean
    }> {
        try {
            const query: IApiFollowLink["query"] = {
                OfferCalculationId: offerCalculationId,
            }

            const dto: (IUpdateCalculationResultRespDto & { id: Guid }) = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.offerCalculation,
                    rel: ApiLinkRel.update,
                    params: { query },
                },
                data
            })

            return { result: new CalculationResult(dto), id: dto.id, isAvailable: true }
        } catch (e) {
            if (e.isAxiosError) {
                const error = e as AxiosError<any>
                if (error.response?.status === 409) {
                    return { result: null, id: null, isAvailable: false }
                }
            }
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async validateOfferCalculationDetailsOverheadFactors(assetId: Guid, date: Dayjs): Promise<CalculationParamsValidation[]> {
        try {
            const dto: IValidateCalculationDetailsOverheadFactorsResp = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.calculationDetailsOverheadValidationAsset,
                    rel: ApiLinkRel.self,
                    params: {
                        query: {
                            assetId,
                            date: toDateTimeWithoutTimezone(date)
                        }
                    },
                }
            })

            return dto.paramsValidationStatus
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getOverheadFactorsSettings(phase: OverheadFactorsPhase): Promise<OverheadFactorsSettings> {

        const query: IApiFollowLink["query"] = {
            phase,
        }

        try {
            const dto: IOverheadFactorsSettingsDto = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.overheadFactorsSettings,
                    rel: ApiLinkRel.self,
                    params: {
                        query
                    }
                },
            })
            return new OverheadFactorsSettings(dto)
        } catch (e) {
            console.log(e)
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getOverheadFactorsPeriods(link: IApiLink, phase: OverheadFactorsPhase): Promise<OverheadFactorsPeriods> {

        const query: IApiFollowLink["query"] = {
            phase
        }

        try {
            const dto: IOverheadFactorsPeriodsDto = await ApiService.followLink({
                link,
                linkParams: {
                    query
                }
            })
            return new OverheadFactorsPeriods(dto)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async createOverheadFactorsPeriod(link: IApiLink, item: ICreateOverheadFactorsPeriodDto): Promise<{ id: Guid }> {
        try {
            return await ApiService.followLink({
                link,
                data: item,
            })
        } catch (e) {
            if (e.isAxiosError && (e as AxiosError<any>).response?.status === 403) {
                throw new CustomError({
                    code: ErrorCode.forbidden
                })
            }
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async updateOverheadFactorsPeriod(link: IApiLink, id: Guid, patch: Operation[], eTag: string = ""): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                linkParams: {
                    id: id,
                },
                data: patch,
                headers: {
                    "If-Match": eTag
                }
            })
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getOverheadFactors(link: IApiLink, overheadFactorsPeriodId: Guid, params?: IGetOverheadFactorsParams): Promise<OverheadFactors> {
        try {
            const query: IApiFollowLink["query"] = {
                AssetCategoryId: params?.assetCategoryId,
                DivisionId: params?.divisionId
            }
            const dto: IOverheadFactorsDto = await ApiService.followLink({
                link,
                linkParams: {
                    id: overheadFactorsPeriodId,
                    query
                },
            })
            return new OverheadFactors(dto)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async createOverheadFactors(link: IApiLink, data: IOverheadFactorDto): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data
            })
        } catch (e) {
            if (e.isAxiosError && (e as AxiosError<any>).response?.status === 409) {
                throw new CustomError({
                    code: ErrorCode.conflict
                })
            }
            if (e.isAxiosError && (e as AxiosError<any>).response?.status === 422) {
                throw new CustomError({
                    code: ErrorCode.unprocessableEntity
                })
            }
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getOverheadFactorsAssetTypes(link: IApiLink, params: { availability?: FinanceServiceAssetTypeAvailability, assetCategoryId?: Guid, divisionId?: Guid }): Promise<FinanceServiceAssetType[]> {
        try {
            const query: IApiFollowLink["query"] = params
            const resp: IGetFinanceServiceAssetTypeDto[] = await ApiService.followLink({
                link,
                linkParams: {
                    query,
                }
            })
            return resp.map(dto => new FinanceServiceAssetType(dto))
        } catch (e) {
            console.log(e)
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getFinancialFactors(): Promise<FinancialFactors> {
        try {
            const dto: IGetFinancialFactorsDto = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.financialFactors,
                    rel: ApiLinkRel.self,
                },
                includeEtag: true
            })
            return new FinancialFactors(dto)
        } catch (e) {
            throw new CustomError({
                messageCode: "message.systemError"
            })
        }
    }

    async updateFinancialFactors(params: IPostFinancialFactorsDto, link: IApiLink, eTag?: string): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: params,
                headers: eTag ? {
                    "If-Match": eTag
                } : undefined
            })
        } catch (e) {
            if (!e.isAxiosError) {
                throw new CustomError({
                    code: ErrorCode.systemError,
                })
            }
            const error = e as AxiosError<any>
            if (error.response && error.response.status === 409) {
                throw new CustomError({
                    code: ErrorCode.conflict,
                })
            } else {
                throw new CustomError({
                    code: ErrorCode.systemError,
                })
            }
        }

    }

    async addImputedInterestRatePeriod(period: IPostImputedInterestRateDto, link: IApiLink) {
        try {

            await ApiService.followLink({
                link,
                data: period
            })
        }
        catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getCostCategories(): Promise<CostCategories> {
        try {
            const resp: IMultipleEntitiesResponse<IGetCostCategoryDto> = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.invoiceCostCategory,
                    rel: ApiLinkRel.all,
                },
            })
            return new CostCategories(resp)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async createCostCategory(link: IApiLink, data: ICreateCostCategoryDto): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }
    async updateCostCategory(link: IApiLink, patch: Operation[]): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: patch
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getDepreciationPeriods(): Promise<DepreciationPeriods> {
        try {
            const dto: IGetDepreciationPeriodsDto = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.depreciationPeriod,
                    rel: ApiLinkRel.all,
                },
            })
            return new DepreciationPeriods(dto)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async createDepreciationPeriod(link: IApiLink, patch: Operation[]): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: patch
            })
        } catch (e) {
            if (e.isAxiosError && (e as AxiosError<any>).response?.status === 409) {
                throw new CustomError({
                    code: ErrorCode.conflict
                })
            }
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getDepreciationAssetCategories(hasDefinedPeriod: boolean): Promise<AssetCategory[]> {
        try {
            const resp: IGetAssetCategoryDto[] = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.assetCategory,
                    rel: ApiLinkRel.all,
                    query: { HasDefinedPeriod: hasDefinedPeriod }
                },
            })
            return resp.map(dto => new AssetCategory(dto))
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getDepreciationMatrix(assetCategoryId: Guid) {
        try {
            const dto: IGetDepreciationMatrixDto = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.assetCategoryDepreciation,
                    rel: ApiLinkRel.all,
                    params: {
                        id: assetCategoryId,
                    },
                },
            })
            return new DepreciationMatrix(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createDepreciationMatrix(link: IApiLink, assetCategoryId: Guid, data: IPostDepreciationMatrixDto) {
        try {
            await ApiService.followLink({
                link,
                linkParams: {
                    id: assetCategoryId
                },
                data
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateDepreciationMatrix(link: IApiLink, assetCategoryId: Guid, data: Operation[]) {
        try {
            await ApiService.followLink({
                link,
                linkParams: {
                    id: assetCategoryId
                },
                data
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getExceptionDepreciationMatrix(link: IApiLink, articleId: Guid) {
        try {
            const dto: IGetExceptionDepreciationMatrixDto = await ApiService.followLink({
                link,
                linkParams: {
                    id: articleId
                }
            })
            return new ExceptionDepreciationMatrix(dto)
        }
        catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createExceptionDepreciationMatrix(link: IApiLink, articleId: Guid, data: IPostExceptionDepreciationMatrixDto) {
        try {
            await ApiService.followLink({
                link,
                linkParams: {
                    id: articleId
                },
                data
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async updateExceptionDepreciationMatrix(link: IApiLink, articleId: Guid, data: Operation[]) {
        try {
            await ApiService.followLink({
                link,
                linkParams: {
                    id: articleId
                },
                data
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getEvaluationParametersSettings(assetOrigin: AssetOrigin): Promise<EvaluationParametersSettings> {
        try {

            const query: IApiFollowLink["query"] = {
                assetOrigin: assetOrigin,
            }

            const dto: IGetEvaluationParametersSettingsDto = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.evaluationParametersSettings,
                    rel: ApiLinkRel.all,
                    params: {
                        query,
                    },
                },
            })
            return new EvaluationParametersSettings(dto)
        } catch (e) {
            console.log(e)
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getEvaluationParametersPeriods(link: IApiLink, assetOrigin: AssetOrigin): Promise<EvaluationParametersPeriods> {
        const query: IApiFollowLink["query"] = {
            assetOrigin: assetOrigin,
        }

        try {
            const dto: IGetEvaluationParametersPeriodsDto = await ApiService.followLink({
                link,
                linkParams: {
                    query
                }
            })
            return new EvaluationParametersPeriods(dto)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async createEvaluationParametersPeriod(link: IApiLink, item: ICreateEvaluationParametersPeriodDto): Promise<{ id: Guid }> {
        try {
            return await ApiService.followLink({
                link,
                data: item,
            })
        } catch (e) {
            if (e.isAxiosError && (e as AxiosError<any>).response?.status === 403) {
                throw new CustomError({
                    code: ErrorCode.forbidden
                })
            }
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async updateEvaluationParametersPeriod(link: IApiLink, id: Guid, patch: Operation[], eTag: string = ""): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                linkParams: {
                    id: id,
                },
                data: patch,
                headers: {
                    "If-Match": eTag
                }
            })
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getEvaluationParameters(link: IApiLink, periodId: Guid, params?: IGetEvaluationParametersParams): Promise<EvaluationParameters> {
        try {
            const query: IApiFollowLink["query"] = {
                AssetCategoryId: params?.assetCategoryId,
                DivisionId: params?.divisionId
            }

            const dto: IGetEvaluationParametersDto = await ApiService.followLink({
                link,
                linkParams: {
                    id: periodId,
                    query,
                }
            })

            return new EvaluationParameters(dto)
        } catch (e) {
            console.log(e)
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async createEvaluationParameter(link: IApiLink, data: ICreateEvaluationParameterDto): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data,
            })
        } catch (e) {
            console.log(e)
            if (e.isAxiosError && (e as AxiosError<any>).response?.status === 409) {
                throw new CustomError({
                    code: ErrorCode.conflict
                })
            }
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getEvaluationParameterAssetTypes(link: IApiLink, params: { availability?: FinanceServiceAssetTypeAvailability, assetCategoryId?: Guid, divisionId?: Guid }): Promise<FinanceServiceAssetType[]> {
        try {
            const query: IApiFollowLink["query"] = params
            const resp: IGetFinanceServiceAssetTypeDto[] = await ApiService.followLink({
                link,
                linkParams: {
                    query,
                }
            })
            return resp.map(dto => new FinanceServiceAssetType(dto))
        } catch (e) {
            console.log(e)
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getAssetCategoriesAndDivisions(link: IApiLink, availability: AssetCategoriesAndDivisionsAvailability): Promise<AssetCategoriesAndDivisions> {
        try {
            const dto: IGetAssetCategoriesAndDivisionsDto = await ApiService.followLink({
                link,
                linkParams: {
                    query: {
                        availability,
                    },
                },
            })

            return new AssetCategoriesAndDivisions(dto);
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getLmids(params: {
        search?: string,
        pageSize?: number,
        pageNumber?: number
    }): Promise<IMultipleEntities<Lmid> | null> {
        try {
            const {
                search,
                pageSize,
                pageNumber
            } = params

            const query: IApiFollowLink["query"] = {
                PageSize: pageSize || 10,
                PageNumber: pageNumber || 1
            }

            if (search?.trim()) {
                query.Criteria = search.trim()
            }

            const data: IMultipleEntitiesResponse<string> = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.financeAssetLmid,
                    rel: ApiLinkRel.all,
                    params: {
                        query
                    }
                },
            })

            return {
                list: data.entities,
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getLmids({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) { }
        return null
    }

    async getSupplierOrderNumbers(params: {
        search?: string,
        pageSize?: number,
        pageNumber?: number
    }): Promise<IMultipleEntities<string> | null> {
        try {
            const {
                search,
                pageSize,
                pageNumber
            } = params

            const query: IApiFollowLink["query"] = {
                PageSize: pageSize || 10,
                PageNumber: pageNumber || 1
            }

            if (search?.trim()) {
                query.Criteria = search.trim()
            }

            const data: IMultipleEntitiesResponse<string> = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.financeSupplierOrderNumber,
                    rel: ApiLinkRel.all,
                    params: {
                        query
                    }
                },
            })

            return {
                list: data.entities,
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getSupplierOrderNumbers({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) { }
        return null
    }

    async getFinanceInternalOrderNumbers(params: {
        search?: string,
        pageSize?: number,
        pageNumber?: number
    }): Promise<IMultipleEntities<string> | null> {
        try {
            const {
                search,
                pageSize,
                pageNumber
            } = params

            const query: IApiFollowLink["query"] = {
                PageSize: pageSize || 10,
                PageNumber: pageNumber || 1
            }

            if (search?.trim()) {
                query.Criteria = search.trim()
            }

            const data: IMultipleEntitiesResponse<string> = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.financeInternalOrderNumber,
                    rel: ApiLinkRel.all,
                    params: {
                        query
                    }
                },
            })

            return {
                list: data.entities,
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getFinanceInternalOrderNumbers({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
            }
        } catch (e) { }
        return null
    }


    async getInvoiceNumbers(params: {
        search?: string,
        pageSize?: number,
        pageNumber?: number
    }): Promise<IMultipleEntities<string> | null> {
        try {

            const {
                search,
                pageSize,
                pageNumber
            } = params

            const query: IApiFollowLink["query"] = {
                PageSize: pageSize,
                PageNumber: pageNumber
            }

            if (search?.trim()) {
                query.Criteria = search.trim()
            }

            const data: IMultipleEntitiesResponse<string> = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.invoiceNumber,
                    rel: ApiLinkRel.all,
                    params: {
                        query
                    }
                },
            })

            return {
                list: data.entities,
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getInvoiceNumbers({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined
            }
        } catch (e) { }
        return null
    }

    async getInvoiceReferenceNumbers(params: {
        search?: string,
        pageSize?: number,
        pageNumber?: number
    }): Promise<IMultipleEntities<string> | null> {
        try {

            const {
                search,
                pageSize,
                pageNumber
            } = params

            const query: IApiFollowLink["query"] = {
                PageSize: pageSize,
                PageNumber: pageNumber
            }

            if (search?.trim()) {
                query.Criteria = search.trim()
            }

            const data: IMultipleEntitiesResponse<string> = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.referenceNumber,
                    rel: ApiLinkRel.all,
                    params: {
                        query
                    }
                },
            })

            return {
                list: data.entities,
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getInvoiceReferenceNumbers({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined
            }
        } catch (e) { }
        return null
    }

    async getInvoiceReferenceTexts(params: {
        search?: string,
        pageSize?: number,
        pageNumber?: number
    }): Promise<IMultipleEntities<string> | null> {
        try {

            const {
                search,
                pageSize,
                pageNumber
            } = params

            const query: IApiFollowLink["query"] = {
                PageSize: pageSize,
                PageNumber: pageNumber
            }

            if (search?.trim()) {
                query.Criteria = search.trim()
            }

            const data: IMultipleEntitiesResponse<string> = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.referenceText,
                    rel: ApiLinkRel.all,
                    params: {
                        query
                    }
                },
            })

            return {
                list: data.entities,
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getInvoiceReferenceTexts({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined
            }
        } catch (e) { }
        return null
    }

    async getAssetIncomingInvoices(params: IGetAssetIncomingInvoicesParams): Promise<IMultipleEntities<AssetInvoiceIncoming, { totalAmount?: AmountWithCurrency }>> {
        try {
            const {
                assetId,
                pageSize,
                pageNumber,
                type,
                costCategoryId,
            } = params

            const query: IApiFollowLink["query"] = {
                PageSize: pageSize,
                PageNumber: pageNumber,
                type,
                costCategoryId,
            }

            const data: IMultipleEntitiesResponse<IGetAssetInvoiceIncomingDto, { totalAmount?: IAmountWithCurrencyDto }> = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.assetIncomingInvoiceInfo,
                    rel: ApiLinkRel.all,
                    query,
                    params: {
                        id: assetId
                    }
                },
            })

            return {
                list: data.entities.map(dto => new AssetInvoiceIncoming(dto)),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getAssetIncomingInvoices({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
                aggregates: { totalAmount: data.aggregates?.totalAmount && new AmountWithCurrency(data.aggregates.totalAmount) }
            }
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getAssetOutgoingInvoicesByRequest(params: {
        assetId: Guid,
        pageSize?: number,
        pageNumber?: number,
        type?: AssetInvoiceOutgoingType

    }): Promise<IMultipleEntities<AssetInvoiceOutgoingByRequest, { totalAmount?: AmountWithCurrency, rentalFeeAmount?: AmountWithCurrency }>> {
        try {
            const {
                assetId,
                pageSize,
                pageNumber,
                type
            } = params

            const query: IApiFollowLink["query"] = {
                PageSize: pageSize,
                PageNumber: pageNumber,
                type
            }

            const data: IMultipleEntitiesResponse<IGetAssetInvoiceOutgoingByRequestDto, { totalAmount?: IAmountWithCurrencyDto, rentalFeeAmount?: IAmountWithCurrencyDto }> = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.assetOutgoingInvoiceInfo,
                    rel: ApiLinkRel.all,
                    params: {
                        id: assetId,
                        query,
                    }
                },
            })

            return {
                list: data.entities.map(dto => new AssetInvoiceOutgoingByRequest(dto)),
                pagination: data.paginationMetadata,
                loadMore: hasMorePages(data.paginationMetadata) ? () => this.getAssetOutgoingInvoicesByRequest({ ...params, pageNumber: data.paginationMetadata!.currentPage + 1 }) : undefined,
                aggregates: { totalAmount: data.aggregates?.totalAmount && new AmountWithCurrency(data.aggregates.totalAmount), rentalFeeAmount: data.aggregates?.rentalFeeAmount && new AmountWithCurrency(data.aggregates.rentalFeeAmount) }
            }
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }


    async validateAssetType(assetId: Guid, assetTypeId: Guid): Promise<void> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.assetTypeValidation,
                    rel: ApiLinkRel.self,
                },
                data: {
                    assetId,
                    assetTypeId,
                }
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async validateEndOwnershipDate(assetId: Guid, assetDisposalDate: Dayjs): Promise<void> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.assetDisposalDateValidation,
                    rel: ApiLinkRel.self,
                },
                data: {
                    assetId,
                    assetDisposalDate: toDateTimeWithoutTimezone(assetDisposalDate),
                }
            })
        } catch (e) {
            if (e.isAxiosError && e.response.status === 422) {
                throw new CustomError({
                    code: ErrorCode.unprocessableEntity,
                })
            } else {
                throw new CustomError({
                    code: ErrorCode.systemError,
                })
            }
        }
    }

    async getMonthlyClosings(): Promise<MonthlyClosings> {
        try {
            const dto: IGetMonthlyClosingsDto = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.monthlyClosing,
                    rel: ApiLinkRel.all,
                },
            })

            return new MonthlyClosings(dto)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async createMonthlyClosing(date: Dayjs, link: IApiLink): Promise<void> {
        const dateIgnoringTimezone = dayjs(date, "YYYY-MM")
        const data: ICreateMonthlyClosingDto = {
            month: dateIgnoringTimezone.month() + 1,
            year: dateIgnoringTimezone.year(),
        }
        try {
            await ApiService.followLink({
                link,
                data,
            })
        } catch (e) {
            if (e.isAxiosError) {
                const error = (e as AxiosError<any>)
                const status = error.response?.status

                throw new CustomError({
                    code: status === 409
                        ? ErrorCode.conflict
                        : status === 403
                            ? ErrorCode.forbidden
                            : ErrorCode.systemError,
                    payload: status === 409 ? {
                        monthIsAlreadyClosed: error.response?.data.monthIsAlreadyClosed,
                        closingInFuture: error.response?.data.closingInFuture,
                        assetSplitOperationInProgress: error.response?.data.assetSplitOperationInProgress,
                        taxBasedDeductionNotDefined: error.response?.data.taxBasedDeductionNotDefined,
                    } : undefined
                })
            }
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async forecastMonthlyClosing(period: string, link: IApiLink): Promise<void> {
        try {
            const resp: { blob: Blob, fileName?: string } = await ApiService.followLink({
                link,
                headers: {
                    "Accept": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                },
                responseType: "blob",
                includeFilename: true,
                linkQuery: {
                    period,

                },

            })

            downloadBlobAsFile(resp.blob, resp.fileName || "")
        }

        catch (e) {
            if (e.isAxiosError) {
                const error = (e as AxiosError<any>)
                const status = error.response?.status

                throw new CustomError({
                    code: status === 409
                        ? ErrorCode.conflict
                        : status === 403
                            ? ErrorCode.forbidden
                            : ErrorCode.systemError,
                    payload: status === 409 ? {
                        monthIsAlreadyClosed: error.response?.data.monthIsAlreadyClosed,
                        closingInFuture: error.response?.data.closingInFuture,
                        assetSplitOperationInProgress: error.response?.data.assetSplitOperationInProgress
                    } : undefined
                })
            }
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async downloadAcquisitionDisposalForecastReport(period: string): Promise<void> {

        const query = { period }
        try {
            const resp: { blob: Blob, fileName?: string } = await ApiService.request({
                headers: {
                    "Accept": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                },
                responseType: "blob",
                includeFilename: true,
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.acquisitionDisposalForecastReport,
                    rel: ApiLinkRel.self,
                    params: { query },
                },
            })

            downloadBlobAsFile(resp.blob, resp.fileName || "")
        }

        catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getBookingPeriod(): Promise<Dayjs | null> {
        try {
            const resp: IGetBookingPeriodResp = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.bookingPeriod,
                    rel: ApiLinkRel.self,
                },
            })

            return dayjs(resp.bookingPeriod, "YYYY-MM")
        } catch (e) {
            if (e.isAxiosError && (e as AxiosError<any>).response?.status === 404) {
                return null
            }
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async downloadReport(link: IApiLink): Promise<void> {
        try {
            const resp: { blob: Blob, fileName?: string } = await ApiService.followLink({
                link,
                headers: {
                    "Accept": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                },
                responseType: "blob",
                includeFilename: true,
            })

            downloadBlobAsFile(resp.blob, resp.fileName || "")
        } catch (e) {
            console.log(e)
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async downloadAssetOverviewReport(): Promise<void> {
        try {
            const resp: { blob: Blob, fileName?: string } = await ApiService.request({
                headers: {
                    "Accept": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                },
                responseType: "blob",
                includeFilename: true,
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.assetOverviewReport,
                    rel: ApiLinkRel.self,
                },
            })

            downloadBlobAsFile(resp.blob, resp.fileName || "")
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getAsyncOperationStatus(link: IApiLink): Promise<AsyncOperation> {
        try {
            const validateStatus = (status) => status >= 200 && status <= 302
            const dto: IGetAsyncOperationStatusDto = await ApiService.followLink({ link: link, validateStatus: validateStatus })
            return new AsyncOperation(dto)
        } catch (e) {
            console.log(e)
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async updateSpecialDepreciation(request: IUpdateSpecialDepreciationDto): Promise<void> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.specialDepreciation,
                    rel: ApiLinkRel.update,
                    params: {
                        id: request.valuationParameterId,
                    },
                },
                data: {
                    depreciationType: request.depreciationType,
                    period: request.period.utcOffset(0, true).toISOString(),
                    amount: request.amount,
                    comment: request.comment,
                }
            })
        } catch (e) {
            if (e.isAxiosError && e.response.status === 422) {
                throw new CustomError({
                    code: ErrorCode.unprocessableEntity,
                })
            } else {
                throw new CustomError({
                    code: ErrorCode.systemError,
                })
            }
        }
    }

    /**
     * Sets the depreciation period IFRS of an asset manually, overriding the calculated value.
     *
     * confirmMinimumOverride waives the minimum-period rule for investment assets after the user
     * confirmed it; it never relaxes the other validation rules, which the service rejects with 422.
     */
    async updateDepreciationPeriodIfrs(request: IUpdateDepreciationPeriodIfrsDto): Promise<void> {
        try {
            await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.depreciationPeriodIfrs,
                    rel: ApiLinkRel.update,
                    params: {
                        id: request.valuationParameterId,
                    },
                },
                data: {
                    period: request.period,
                    comment: request.comment,
                    confirmMinimumOverride: request.confirmMinimumOverride,
                }
            })
        } catch (e) {
            if (e.isAxiosError && e.response.status === 422) {
                throw new CustomError({
                    code: ErrorCode.unprocessableEntity,
                })
            } else {
                throw new CustomError({
                    code: ErrorCode.systemError,
                })
            }
        }
    }

    async getSpecialDepreciationBookValue(request: IReadSpecialDepreciationBookValueDto): Promise<AmountWithCurrency> {
        try {
            const query: IApiFollowLink["query"] = {
                depreciationType: request.depreciationType,
                period: request.period.utcOffset(0, true).toISOString(),
                amount: request.amount.amount,
            }

            const dto: IAmountWithCurrencyDto = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.specialDepreciationBookValue,
                    rel: ApiLinkRel.self,
                    params: {
                        id: request.valuationParameterId,
                        query
                    },
                },
            })

            return new AmountWithCurrency(dto)
        } catch (e) {
            console.log(e)
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getDivisions(): Promise<Division[]> {
        try {
            const dtos: IGetDivisionDto[] = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.division,
                    rel: ApiLinkRel.all,
                },
            })

            return dtos.map(dto => new Division(dto))
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getFairValueFactors(): Promise<FairValueFactors> {
        try {
            const dto: IGetFairValueFactorsDto = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.fairValueFactors,
                    rel: ApiLinkRel.all,
                },
            })

            return new FairValueFactors(dto)
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async createFairValueFactors(link: IApiLink,
        params:
            {
                articleId?: Guid,
                assetCategoryId?: Guid,
                divisionId?: Guid,
                value: IPostFairValueFactorsDto
            }
    ): Promise<Guid> {
        const query = {
            ArticleId: params.articleId,
            AssetCategoryId: params.assetCategoryId,
            DivisionId: params.divisionId,
        }
        let id
        try {
            const data = await ApiService.followLink({
                link,
                data: params.value,
                linkQuery: query
            })
            id = data.id
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }

        return id
    }

    async updateFairValueFactors(link: IApiLink,
        params:
            {
                id?: Guid,
                value: Operation[]
            }
    ): Promise<void> {
        const query = {
            id: params.id
        }
        try {
            await ApiService.followLink({
                link,
                data: params.value,
                linkQuery: query
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }


    async getTaxBasedDeductions(assetOrigin: AssetOrigin): Promise<TaxBasedDeductions> {
        try {
            const dto: ITaxBasedDeductionsDto = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.taxBasedDeduction,
                    rel: ApiLinkRel.all,
                    params: {
                        query: {
                            assetOrigin,
                        },
                    },
                },
            })

            return new TaxBasedDeductions(dto)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async createTaxBasedDeduction(link: IApiLink, item: ICreateTaxBasedDeductionDto): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: item,
            })
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getAssetReadyToSplit(assetId: Guid): Promise<AssetReadyToSplit> {
        try {
            const dto: IGetAssetReadyToSplitDto = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.assetReadinessToSplit,
                    rel: ApiLinkRel.self,
                    params: {
                        id: assetId
                    },
                },
            })

            return new AssetReadyToSplit(dto)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async uploadInvoiceImportExcelFile(link: IApiLink, file: File): Promise<void> {
        try {
            const data = new FormData()
            data.append("file", file)
            await ApiService.followLink({ link, data })
        } catch (e) {
            if (e.isAxiosError) {
                const error = e as AxiosError<any>
                if (error.response?.status === 409) {
                    throw new CustomError({
                        code: ErrorCode.conflict,
                    })
                }
                if (error.response?.status === 422) {
                    throw new CustomError<InvoiceImportError[]>({
                        code: ErrorCode.unprocessableEntity,
                        payload: Object.keys(error.response.data ?? []) as InvoiceImportError[],
                    })
                }
            }
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async uploadInvoiceImportFromErp(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({ link })
        } catch (e) {
            if (e.isAxiosError) {
                const error = e as AxiosError<any>
                if (error.response?.status === 409) {
                    throw new CustomError({
                        code: ErrorCode.conflict,
                    })
                }
                if (error.response?.status === 422) {
                    throw new CustomError<InvoiceImportError[]>({
                        code: ErrorCode.unprocessableEntity,
                        payload: Object.keys(error.response.data ?? []) as InvoiceImportError[],
                    })
                }
            }
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async getInvoiceImportOperation(): Promise<InvoiceImportOperation> {
        try {
            const data: IGetInvoiceImportOperationDto = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.invoiceImportOperation,
                    rel: ApiLinkRel.self,
                },
            })

            return new InvoiceImportOperation(data)
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError,
            })
        }
    }

    async linkInvoiceImportItemToAsset(link: IApiLink, assetId: Guid) {
        try {
            await ApiService.followLink({
                link,
                data: { assetId },
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async deleteInvoiceImportOperation(link: IApiLink): Promise<void> {
        try {
            await ApiService.followLink({ link })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async deleteInvoiceImportItems(link: IApiLink, assetImportItemIds: Guid[]): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: assetImportItemIds,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async importInvoiceImportItems(link: IApiLink, assetImportItemIds: Guid[]): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: assetImportItemIds,
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getAssetsAcquisition(assetIds: Guid[]): Promise<AssetAcquisition[]> {
        if (assetIds.length === 0) {
            return []
        }
        try {
            const resp: IGetAssetAcqiusitionDTO[] = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.assetAcquisition,
                    rel: ApiLinkRel.all,
                },
                data: assetIds,
            })

            return resp.map(dto => new AssetAcquisition(dto))
        } catch (e) {
            throw new CustomError({
                code: ErrorCode.systemError
            })
        }
    }

    async getFairValueEvaluationPeriods(userProfitCenterIds: Guid[]) {
        try {
            const data: IFairValueEvaluationPeriod[] = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.fairValueEvaluationPeriod,
                    rel: ApiLinkRel.all,
                },
                data: { userProfitCenterIds },
            })
            return data
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getFairValueEvaluation(bookingPeriod: string, params: FairValueEvaluationParams, signal: AbortSignal): Promise<IMultipleEntities<FairValueReferenceNumberEvaluationItem, { links: IApiLink[] }>> {
        try {
            const dto: IMultipleEntitiesResponse<IGetFairValueReferenceNumberEvaluationItem> = await ApiService.request({
                serviceName: ApiServiceName.finance,
                followLink: {
                    entity: ApiLinkEntity.fairValueEvaluation,
                    rel: ApiLinkRel.all,
                    params: {
                        bookingPeriod
                    }
                },
                data: {
                    userProfitCenterIds: params.userProfitCenterIds,
                    profitCenterIds: params.profitCenterIds,
                    assetCategoryIds: params.assetCategories,
                    referenceNumberSales: params.referenceNumberSale,
                    missingDepreciationRate: params.missingDepreciationRate,
                    onlyReferenceNumbersWithMachines: params.onlyReferenceNumbersWithMachines,
                    pageSize: params.pageSize,
                    pageNumber: params.pageNumber,
                    sortBy: params.sortBy,
                    sortDirection: params.sortDirection
                },
                signal
            })
            return {
                ...dto,
                list: dto.entities.map(entity => new FairValueReferenceNumberEvaluationItem(entity)),
                aggregates: { links: dto.links ?? [] },
                loadMore: hasMorePages(dto.paginationMetadata) ? () => this.getFairValueEvaluation(bookingPeriod, { ...params, pageNumber: dto.paginationMetadata!.currentPage + 1 }, signal) : undefined,
            }

        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async checkFairValueEvaluation(link: IApiLink, userProfitCenterIds: Guid[]): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: { userProfitCenterIds },
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }

    async getFairValueEvaluationBookValues(link: IApiLink, assetIds: Guid[], signal: AbortSignal): Promise<FairValueEvaluationAssetBookValue[]> {
        try {
            const dtos: IGetFairValueEvaluationAssetBookValue[] = await ApiService.followLink({
                link,
                data: assetIds,
                signal
            })
            return dtos.map(dto => new FairValueEvaluationAssetBookValue(dto))
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }


    async setEstimates(link: IApiLink, userProfitCenterIds: Guid[], data: { estimatedNetSalePrice: IAmountWithCurrencyDto | null, estimatedSDC: IAmountWithCurrencyDto | null, comment: string | null }): Promise<void> {
        try {
            await ApiService.followLink({
                link,
                data: {
                    userProfitCenterIds,
                    estimatedNetSalePrice: data.estimatedNetSalePrice,
                    estimatedSDC: data.estimatedSDC,
                    comment: data.comment
                },
            })
        } catch (e) {
            ErrorHandler.handleHttpError(e)
        }
    }
}

export default new FinanceService()
